/*
 * Functions in this file:
 * parseXMLPolicy: (fileString: string) => ErrorXMLParsing | PolicyComponents
 * * Exported function that takes in a files string of XML constructs a DOMParsers and gets all the self asserted user data
 *
 * getUserInputsFromPolicy: (xml: Readonly<Document>) => Record<string, Claim> | ErrorXMLParsing
 * * Helper function that parses all Claims Schema for all expected user inputs and there associated types and if needed enumerated values
 *
 * getEmailVerificationDisplayControlsFromPolicy: (xml: Readonly<Document>) => ErrorXMLParsing | { additionalclaims: Record<string, Claim>, displayControlItems: Record<string, Array<string>> }
 * * Helper function that parses all display controls and garbs the information for all email verification display controls
 *
 * getTechnicalProfileDefinitionsFromPolicy: (xml: Readonly<Document>, parsedXMLComponents: Readonly<PolicyComponents>, displayControlComponents: Readonly<Record<string, Array<string>>>) => Record<string, Array<string>> | ErrorXMLParsing
 * * Helper function that constructs pages from technical profiles using the user inputs and display controls previously parsed
 *
 * constructCombinedSISUPages: (xml: Readonly<Document>, parsedXMLComponents: Readonly<PolicyComponents>) => { additionalclaims: Record<string, Claim>, modifiedprofiles: Record<string, Array<string>> }
 * * Helper function that detects Combined SISU pages and applys the unique attributes to them
 *
 * getUserJourneysFromPolicy: (xml: Readonly<Document>, tempprofileNames: ReadonlyArray<string>) => Record<string, Array<string>> | ErrorXMLParsing
 * * Helper function that contructs user journeys using the detected pages
 */
import { ErrorXMLParsing } from '../utils/ErrorReturn';
import {
  init_ClaimProvider,
  init_ClaimsExhange,
  init_ClaimsTransformation,
  init_InputClaim,
  init_OrchestrationStep,
  init_OutputClaim,
  init_PolicyComponents,
  init_PreconditionOrchestration,
  init_PreconditionTP,
  init_ProviderSelect,
  init_NonInteractiveTechnicalProfile,
  init_SelfAssertedProfile,
  init_SubJourney,
  init_Transformation,
  init_TransformationInputParams,
  init_UserJourney,
  init_ValidationTP,
  stringToPreconditionType,
  init_DisplayClaim,
} from './initInterfaces';
import {
  PolicyComponents,
  Claim,
  SelfAssertedProfile,
  NonInteractiveTechnicalProfile,
  InputClaim,
  PersistedClaim,
  OutputClaim,
  DisplayClaim,
  Precondition,
  PreconditionAction,
  ClaimsTransformation,
  UserJourney,
  SubJourney,
  OrchestrationStep,
  ValidationTP,
  ClaimProvider,
  AllPolicyXML,
  RPFileComponents,
  UserInfoEndpoint,
  Enumeration,
  Pattern,
} from './interfaces';

const serializerForErrors = new XMLSerializer();

export const parseXMLPolicy = (
  fileStrings: Array<{ name: string; fileString: string }>,
): ErrorXMLParsing | AllPolicyXML => {
  // DOMParser to go over XML policy
  const parser = new DOMParser();

  let fullB2CEnv: AllPolicyXML = {
    policyComponents: {},
    rpComponents: {},
    rpInheritance: {},
  };

  // { polciyName: [xmlDocument, raw file string, policy it inherits from, is RP file?]}
  let parsedFiles: Record<string, [Document, string, string, boolean]> = {};

  try {
    fileStrings.forEach((file) => {
      const xml = parser.parseFromString(file.fileString, 'text/xml');
      const policyId =
        xml.getElementsByTagName('TrustFrameworkPolicy').length === 1
          ? xml.getElementsByTagName('TrustFrameworkPolicy')[0].getAttribute('PolicyId')
          : '';

      if (policyId === null) {
        throw new ErrorXMLParsing(
          `File ${file.name} does not have a PolicyId attribute on its TrustFrameworkPolicy Element.`,
          'TrustFrameworkPolicy',
        );
      }
      if (policyId === '') {
        throw new ErrorXMLParsing(
          `File ${file.name} does not have a TrustFrameworkPolicy Element (or has more than one?).`,
          'TrustFrameworkPolicy',
        );
      }

      const basePolicy = xml.getElementsByTagName('BasePolicy');

      if (basePolicy.length > 1)
        throw new ErrorXMLParsing(
          `File ${file.name} with PolicyId ${policyId} has too many BasePolicy Elements, only one is allowed`,
          'TrustFrameworkPolicy>BasePolicy',
        );

      if (basePolicy.length === 1 && basePolicy[0].getElementsByTagName('PolicyId').length !== 1)
        throw new ErrorXMLParsing(
          `File ${file.name} with PolicyId ${policyId} must have exactly 1 PolicyId element as a child of the BasePolicy element`,
          'TrustFrameworkPolicy>BasePolicy>PolicyId',
        );

      const policyInheritance =
        basePolicy.length === 1 ? basePolicy[0].getElementsByTagName('PolicyId')[0].textContent : '';

      if (policyInheritance === null)
        throw new ErrorXMLParsing(
          `File ${file.name} and PolicyId ${policyId}'s BasePolicy element must have text inside the PolicyId element indicating which other policy to inherit from.`,
          'TrustFrameworkPolicy>BasePolicy>PolicyId',
        );

      const relyingParty = xml.getElementsByTagName('RelyingParty');

      if (relyingParty.length > 1)
        throw new ErrorXMLParsing(
          `File ${file.name} and PolicyId ${policyId} has more than 1 RelyingParty Element.`,
          'TrustFrameworkPolicy>RelyingParty',
        );

      parsedFiles[policyId] = [xml, file.fileString, policyInheritance, relyingParty.length === 1];
    });

    Object.keys(parsedFiles).forEach((key) => {
      if (parsedFiles[key][3]) {
        // Generate to rpInheritance object
        fullB2CEnv.rpInheritance[key] = [];
        // Check for each file if base policy exists
        let currentPolicy = key;
        let nextInheritance = parsedFiles[key][2];

        //RP file needs to inherit from something
        if (nextInheritance === '') {
          throw new ErrorXMLParsing(
            `RP File ${key} does not have a base policy to inherit from.`,
            'TrustFrameworkPolicy>BasePolicy>PolicyId',
          );
        }

        while (nextInheritance !== '') {
          if (parsedFiles[nextInheritance] === undefined) {
            throw new ErrorXMLParsing(
              `Policy ${currentPolicy} is trying to inherit from base policy ${nextInheritance}, but that file either does not exist or was not provided.`,
              'TrustFrameworkPolicy>BasePolicy>PolicyId',
            );
          }

          fullB2CEnv.rpInheritance[key].push(nextInheritance);
          currentPolicy = nextInheritance;
          nextInheritance = parsedFiles[nextInheritance][2];
        }
      }
    });

    Object.keys(fullB2CEnv.rpInheritance).forEach((rpFile) => {
      let currentRPInheritance = fullB2CEnv.rpInheritance[rpFile];
      let currentlyInherited: Record<string, PolicyComponents> = {};

      for (let i = currentRPInheritance.length - 1; i >= 0; i--) {
        let policyToParse = currentRPInheritance[i];
        // Check if we have already parsed it
        if (fullB2CEnv.policyComponents[policyToParse] !== undefined) {
          currentlyInherited[policyToParse] = fullB2CEnv.policyComponents[policyToParse];
          continue;
        }

        let newParsedPolicy = parseSingleXMLFile(
          policyToParse,
          parsedFiles[policyToParse][0],
          parsedFiles[policyToParse][1],
          parsedFiles[policyToParse][2],
          currentlyInherited,
        );

        currentlyInherited[policyToParse] = newParsedPolicy;
        fullB2CEnv.policyComponents[policyToParse] = newParsedPolicy;
      }

      // Parse RP file properly later (for now just parsing as normal policy - RP element is not Read)
      let rpElement =
        parsedFiles[rpFile][0].getElementsByTagName('RelyingParty').length > 0
          ? parsedFiles[rpFile][0].getElementsByTagName('RelyingParty')[0]
          : null;
      if (rpElement === null) {
        throw new ErrorXMLParsing(
          `RP File ${rpFile} does not have a RelyingParty element!`,
          `${rpFile}: TrustFrameworkPolicy`,
        );
      }
      let defaultUJ =
        rpElement.getElementsByTagName('DefaultUserJourney').length > 0
          ? getRequiredElementAttribute(
              rpElement.getElementsByTagName('DefaultUserJourney')[0],
              'ReferenceId',
              rpFile,
              `${rpFile}: TrustFrameworkPolicy > RelyingParty > DefaultUserJourney`,
            )
          : null;

      if (defaultUJ === null) {
        throw new ErrorXMLParsing(
          `RP File ${rpFile} does not have a DefaultUserJourney element!`,
          `${rpFile}: TrustFrameworkPolicy > RelyingParty`,
        );
      }

      let EndpointsRoot =
        rpElement.getElementsByTagName('Endpoints').length > 0 ? rpElement.getElementsByTagName('Endpoints')[0] : null;
      let Endpoints: UserInfoEndpoint[] = [];
      if (EndpointsRoot) {
        if (EndpointsRoot.children.length < 1) {
          throw new ErrorXMLParsing(
            `RP File ${rpFile}: No endpoint found in Endpoints, either add an endpoint or remove the empty Endpoints element`,
            `${rpFile}: TrustFrameworkPolicy > RelyingParty > Endpoints`,
          );
        }
        Endpoints = Array.from(EndpointsRoot.children).map((el) => {
          let Id = getRequiredElementAttribute(
            el,
            'Id',
            rpFile,
            `${rpFile}: TrustFrameworkPolicy > RelyingParty > Endpoints > Endpoint`,
          );
          let UserJourneyReferenceId = getRequiredElementAttribute(
            el,
            'UserJourneyReferenceId',
            rpFile,
            `${rpFile}: TrustFrameworkPolicy > RelyingParty > Endpoints > Endpoint`,
          );
          return { Id, UserJourneyReferenceId };
        });
      }

      let UserJourneyBehaviors;

      let TechnicalProfile = '';

      let newParsedPolicy: RPFileComponents = {
        defaultUserJourney: defaultUJ,
        Endpoints,
        UserJourneyBehaviors,
        TechnicalProfile,
        ...parseSingleXMLFile(
          rpFile,
          parsedFiles[rpFile][0],
          parsedFiles[rpFile][1],
          parsedFiles[rpFile][2],
          currentlyInherited,
        ),
      };

      fullB2CEnv.rpComponents[rpFile] = newParsedPolicy;
    });

    Object.keys(parsedFiles).forEach((key) => {
      if (fullB2CEnv.policyComponents[key] === undefined && fullB2CEnv.rpComponents[key] === undefined) {
        let newParsedPolicy = parseSingleXMLFile(
          key,
          parsedFiles[key][0],
          parsedFiles[key][1],
          parsedFiles[key][2],
          {},
        );
        fullB2CEnv.policyComponents[key] = newParsedPolicy;
      }
    });

    return fullB2CEnv;
  } catch (e: any) {
    return e;
  }
};

/**
 *
 * @param {string} fileString - a string of XML
 * @returns {(ErrorXMLParsing | PolicyComponents)} the XML deconstructed into usable building blocks
 */
export const parseSingleXMLFile = (
  name: string,
  xml: Document,
  fileString: string,
  inheritingPolicy: string,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
): PolicyComponents => {
  let parsedXMLComponents: PolicyComponents = init_PolicyComponents();

  parsedXMLComponents.basePolicy = inheritingPolicy;

  let claimsFromXML: Record<string, Claim> = getUserInputsFromPolicy(
    xml,
    name,
    Object.keys(inheritedPolicies).length === 0,
  );
  parsedXMLComponents.claims = claimsFromXML;

  let claimsTransformation: Record<string, ClaimsTransformation> = getClaimsTransformationsFromPolicy(
    xml,
    name,
    parsedXMLComponents,
    inheritedPolicies,
  );
  parsedXMLComponents.claimTransformations = claimsTransformation;

  let profiles: {
    selfAsserted: Record<string, SelfAssertedProfile>;
    other: Record<string, NonInteractiveTechnicalProfile>;
    claimsProviders: Array<ClaimProvider>;
  } = getTechnicalProfileDefinitionsFromPolicy(xml, parsedXMLComponents, name, inheritedPolicies);

  parsedXMLComponents.selfAssertedProfiles = profiles.selfAsserted;
  parsedXMLComponents.otherProfiles = profiles.other;
  parsedXMLComponents.claimProviders = profiles.claimsProviders;

  let userJourneys: Array<UserJourney> = getUserJourneysFromPolicy(xml, name);

  parsedXMLComponents.userJourneys = userJourneys;
  let subJourneys: Array<SubJourney> = getSubJourneysFromPolicy(xml, name);

  parsedXMLComponents.subJourneys = subJourneys;

  let xmlSerializer = new XMLSerializer();

  parsedXMLComponents.rawXMLString = xmlSerializer.serializeToString(xml);

  return parsedXMLComponents;
};

const resolveClaimAgainstClaimsSchema = (
  claimName: string,
  policyComponents: PolicyComponents,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
): string | undefined => {
  let claimValues = Object.keys(policyComponents.claims);
  let existsInPolicy = claimValues.find((claim) => claim.toLowerCase() === claimName.toLowerCase());
  if (!existsInPolicy) {
    Object.keys(inheritedPolicies).find((policyName) => {
      Object.keys(inheritedPolicies[policyName].claims).some((claim: string) => {
        if (claim.toLowerCase() === claimName.toLowerCase()) existsInPolicy = claim;
      });
    });
  }
  return existsInPolicy;
};

const resolveClaimTransformationsAgainstClaimsTransformationSchema = (
  claimTransformationName: string,
  policyComponents: PolicyComponents,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
): string | undefined => {
  let claimTransValues = Object.keys(policyComponents.claimTransformations);
  let existsInPolicy = claimTransValues.find(
    (claimTrans) => claimTrans.toLowerCase() === claimTransformationName.toLowerCase(),
  );

  if (!existsInPolicy) {
    Object.keys(inheritedPolicies).find((policyName) => {
      Object.keys(inheritedPolicies[policyName].claimTransformations).some((claimTrans: string) => {
        if (claimTrans.toLowerCase() === claimTransformationName.toLowerCase()) existsInPolicy = claimTrans;
      });
    });
  }
  return existsInPolicy;
};
const checkInheritedForClaims = (
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
  claimName: string,
): boolean => {
  return Object.keys(inheritedPolicies).some(
    (policyName) => inheritedPolicies[policyName].claims[claimName] !== undefined,
  );
};

const checkInheritedForClaimsTransformation = (
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
  claimTransformationName: string,
): boolean => {
  return Object.keys(inheritedPolicies).some(
    (policyName) => inheritedPolicies[policyName].claimTransformations[claimTransformationName] !== undefined,
  );
};

const getPossibleElementAttribute = (element: Readonly<Element>, attribute: string): string => {
  let val = element.getAttribute(attribute);

  if (val === null) {
    val = '';
  }

  return val;
};

export const getRequiredElementAttribute = (
  element: Readonly<Element>,
  attribute: string,
  name: string,
  xmlTrace: string,
): string => {
  let val = element.getAttribute(attribute);

  if (val === null) {
    throw new ErrorXMLParsing(
      `Policy ${name}: Attribute ${attribute} does not exist on the element: ${serializerForErrors.serializeToString(
        element,
      )}`,
      xmlTrace,
    );
  }

  return val;
};

const getTextContentOfChild = (
  element: Readonly<Element> | undefined,
  name: string,
  xmlTrace: string,
  required = false,
): string => {
  if (element === undefined) {
    if (required)
      throw new ErrorXMLParsing(`Policy ${name}: The element whose text content was requested is undefined`, xmlTrace);
    else return '';
  }

  let textContent = element.textContent;

  if (textContent === null) {
    throw new ErrorXMLParsing(
      `Policy ${name}: textContent for the element returned null in the following element: ${serializerForErrors.serializeToString(
        element,
      )}`,
      xmlTrace,
    );
  }

  if (required && textContent === '') {
    throw new ErrorXMLParsing(
      `Policy ${name}: textContent for a required element is empty in the following element: ${serializerForErrors.serializeToString(
        element,
      )}`,
      xmlTrace,
    );
  }

  return textContent;
};

const getUserInputsFromPolicy = (xml: Readonly<Document>, name: string, baseFile: boolean): Record<string, Claim> => {
  var inputClaims = Array.from(xml.querySelectorAll('ClaimsSchema>ClaimType'));
  var xmlTrace = `${name}: TrustFrameworkPolicy>ClaimsSchema`;

  if (inputClaims.length === 0 && baseFile) {
    throw new ErrorXMLParsing(`No user inputs found in Claims Schema for policy ${name}.`, xmlTrace);
  }

  /* For each figure out which type of input we are dealing with
       ('null' is for elements we assume are noninteractable, but may have relevant info)
  */
  var claims: Record<string, Claim> = {};
  for (let input of inputClaims) {
    // B2C is case insensitive? In that case lowercase all claims
    var inputId = getRequiredElementAttribute(input, 'Id', name, xmlTrace);
    xmlTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'ClaimType', inputId);

    var displayName = getTextContentOfChild(input.getElementsByTagName('DisplayName')[0], name, xmlTrace);

    var userInputType = getTextContentOfChild(input.getElementsByTagName('UserInputType')[0], name, xmlTrace);

    var inputDataType = getTextContentOfChild(input.getElementsByTagName('DataType')[0], name, xmlTrace);

    // Place element in policy inputs data structure
    claims[inputId] = {
      id: inputId,
      displayName: displayName,
      userInputType: userInputType,
      dataType: inputDataType,
    };

    if (input.getElementsByTagName('UserHelpText').length > 0) {
      let userHelpText: string = getTextContentOfChild(input.getElementsByTagName('UserHelpText')[0], name, xmlTrace);
      claims[inputId].userHelpText = userHelpText;
    }

    if (input.getElementsByTagName('AdminHelpText').length > 0) {
      let adminHelpText: string = getTextContentOfChild(input.getElementsByTagName('AdminHelpText')[0], name, xmlTrace);
      claims[inputId].adminHelpText = adminHelpText;
    }

    if (input.getElementsByTagName('Mask').length > 0) {
      let mask = input.getElementsByTagName('Mask')[0];
      claims[inputId].mask = {
        type: mask.getAttribute('Type') || 'Simple',
        regex: mask.getAttribute('Regex') || '',
        value: mask.innerHTML || '',
      };
    }

    //Dropdowns and checkboxes have enumerated options that we need to populate
    if (['CheckboxMultiSelect', 'DropdownSingleSelect', 'RadioSingleSelect'].includes(userInputType)) {
      var enumeratedOptions: Enumeration[] = [];
      if (input.getElementsByTagName('Restriction').length > 0) {
        for (let enumeration of Array.from(input.getElementsByTagName('Enumeration'))) {
          let enumerationValue = getRequiredElementAttribute(enumeration, 'Value', name, xmlTrace);
          let enumerationText = getRequiredElementAttribute(enumeration, 'Text', name, xmlTrace);
          enumeratedOptions.push({
            value: enumerationValue,
            text: enumerationText,
            selectByDefault: getPossibleElementAttribute(enumeration, 'SelectByDefault') === 'true',
          });
        }
      }
      claims[inputId].restriction = 'Enumeration';
      claims[inputId].enumeration = enumeratedOptions;
    }
    if (['TextBox'].includes(userInputType)) {
      if (input.getElementsByTagName('Restriction').length > 0) {
        let pattern = input.getElementsByTagName('Pattern')[0];
        claims[inputId].restriction = 'Pattern';
        claims[inputId].pattern = {
          regularExpression: pattern.getAttribute('RegularExpression') || '',
          helpText: pattern.getAttribute('HelpText') || '',
        };
      }
    }
  } // End of inputClaims.forEach

  return claims;
}; // End of getUserInputsFromPolicy

const getClaimsTransformationsFromPolicy = (
  xml: Readonly<Document>,
  name: string,
  parsedXMLComponents: Readonly<PolicyComponents>,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
): Record<string, ClaimsTransformation> => {
  let claimTransformations: Record<string, ClaimsTransformation> = {};
  let parentXmlTrace = `${name}: BuildingBlocks>ClaimTransformations`;

  if (xml.querySelectorAll('ClaimsTransformations').length > 0) {
    const claimTransformationsFromPolicy = Array.from(
      xml.querySelectorAll('ClaimsTransformations>ClaimsTransformation'),
    );

    for (let ct of claimTransformationsFromPolicy) {
      let newClaimsTransformation = init_ClaimsTransformation();

      newClaimsTransformation.id = getRequiredElementAttribute(ct, 'Id', name, parentXmlTrace);

      let xmlTrace = ErrorXMLParsing.nextXMLTrace(parentXmlTrace, 'ClaimsTransformation', newClaimsTransformation.id);
      newClaimsTransformation.transformationmethod = getRequiredElementAttribute(
        ct,
        'TransformationMethod',
        name,
        xmlTrace,
      );

      for (let inputCT of Array.from(ct.getElementsByTagName('InputClaim'))) {
        let newInputCT = init_Transformation();
        let cRefId = getRequiredElementAttribute(inputCT, 'ClaimTypeReferenceId', name, xmlTrace);
        let inputId: string | undefined = cRefId;
        if (parsedXMLComponents.claims[inputId] === undefined)
          inputId = resolveClaimAgainstClaimsSchema(inputId, parsedXMLComponents, inheritedPolicies);
        if (inputId === undefined)
          throw new ErrorXMLParsing(
            `Policy ${name}: ClaimsTransformation ${newClaimsTransformation.id} has an input claim ${cRefId} that does not match any ClaimSchema`,
            xmlTrace,
          );
        newInputCT.claimReferenceId = cRefId;
        newInputCT.transformationClaimType = getRequiredElementAttribute(
          inputCT,
          'TransformationClaimType',
          name,
          xmlTrace,
        );

        newClaimsTransformation.inputClaims.push(newInputCT);
      }

      for (let iparamCT of Array.from(ct.getElementsByTagName('InputParameter'))) {
        let newIParamCT = init_TransformationInputParams();
        newIParamCT.id = getRequiredElementAttribute(iparamCT, 'Id', name, xmlTrace);
        newIParamCT.dataType = getRequiredElementAttribute(iparamCT, 'DataType', name, xmlTrace);
        newIParamCT.value = getRequiredElementAttribute(iparamCT, 'Value', name, xmlTrace);

        newClaimsTransformation.inputParameters.push(newIParamCT);
      }

      for (let outputCT of Array.from(ct.getElementsByTagName('OutputClaim'))) {
        let newOutputCT = init_Transformation();
        let cRefId = getRequiredElementAttribute(outputCT, 'ClaimTypeReferenceId', name, xmlTrace);
        let outputId: string | undefined = cRefId;
        if (parsedXMLComponents.claims[outputId] === undefined)
          outputId = resolveClaimAgainstClaimsSchema(outputId, parsedXMLComponents, inheritedPolicies);
        if (outputId === undefined)
          throw new ErrorXMLParsing(
            `Policy ${name}: ClaimsTransformation ${newClaimsTransformation.id} has an output claim ${cRefId} that does not match any ClaimSchema`,
            xmlTrace,
          );
        newOutputCT.claimReferenceId = outputId;
        newOutputCT.transformationClaimType = getRequiredElementAttribute(
          outputCT,
          'TransformationClaimType',
          name,
          xmlTrace,
        );

        newClaimsTransformation.outputClaims.push(newOutputCT);
      }

      claimTransformations[newClaimsTransformation.id] = { ...newClaimsTransformation };
    }
  }

  return claimTransformations;
};

const constuctInputClaim = (inputElement: Element, name: string, xmlTrace: string, inputId?: string): InputClaim => {
  let inputClaim: InputClaim = init_InputClaim();

  if (!inputId) {
    inputId = getRequiredElementAttribute(inputElement, 'ClaimTypeReferenceId', name, xmlTrace);
  }

  //let currentXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'InputClaim', inputId);

  inputClaim.claimTypeReferenceId = inputId;
  inputClaim.partnerClaimType = getPossibleElementAttribute(inputElement, 'PartnerClaimType');
  inputClaim.defaultValue = getPossibleElementAttribute(inputElement, 'DefaultValue');
  inputClaim.required = getPossibleElementAttribute(inputElement, 'Required') === 'true';
  inputClaim.alwaysUseDefaultValue = getPossibleElementAttribute(inputElement, 'AlwaysUseDefaultValue') === 'true';

  return inputClaim;
};

const constuctOutputClaim = (
  outputElement: Element,
  name: string,
  xmlTrace: string,
  outputId?: string,
): OutputClaim => {
  let outputClaim: OutputClaim = init_OutputClaim();

  if (!outputId) {
    outputId = getRequiredElementAttribute(outputElement, 'ClaimTypeReferenceId', name, xmlTrace);
  }

  //let currentXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'OutputClaim', outputId);

  outputClaim.claimTypeReferenceId = outputId;
  outputClaim.partnerClaimType = getPossibleElementAttribute(outputElement, 'PartnerClaimType');
  outputClaim.defaultValue = getPossibleElementAttribute(outputElement, 'DefaultValue');
  outputClaim.required = getPossibleElementAttribute(outputElement, 'Required') === 'true';
  outputClaim.alwaysUseDefaultValue = getPossibleElementAttribute(outputElement, 'AlwaysUseDefaultValue') === 'true';

  return outputClaim;
};

const mapCommonTPTags = (
  profile: Element,
  profileObject: SelfAssertedProfile | NonInteractiveTechnicalProfile,
  parsedXMLComponents: Readonly<PolicyComponents>,
  xmlTrace: string,
  name: string,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
) => {
  var displayName = getTextContentOfChild(profile.getElementsByTagName('DisplayName')[0], name, xmlTrace);

  profileObject.displayName = displayName;

  var domain = getTextContentOfChild(profile.getElementsByTagName('Domain')[0], name, xmlTrace);

  profileObject.domain = domain;

  var description = getTextContentOfChild(profile.getElementsByTagName('Description')[0], name, xmlTrace);

  profileObject.description = description;

  if (profile.getElementsByTagName('Protocol').length > 0) {
    let protocolElement = profile.getElementsByTagName('Protocol')[0];
    let protocolName = getRequiredElementAttribute(protocolElement, 'Name', name, xmlTrace);

    profileObject.protocol[0] = protocolName;
    profileObject.protocol[1] = getPossibleElementAttribute(protocolElement, 'Handler');
  }

  if (profile.getElementsByTagName('IncludeTechnicalProfile').length > 0) {
    var includeTP = profile.getElementsByTagName('IncludeTechnicalProfile');
    profileObject.includeTechnicalProfile = getRequiredElementAttribute(includeTP[0], 'ReferenceId', name, xmlTrace);
  }

  if (profile.getElementsByTagName('Metadata').length > 0) {
    let metadataItems = profile.getElementsByTagName('Metadata')[0].getElementsByTagName('Item');

    for (let metadataItem of Array.from(metadataItems)) {
      let key = getRequiredElementAttribute(metadataItem, 'Key', name, xmlTrace);

      let metadataContents = getTextContentOfChild(metadataItem, name, xmlTrace);

      profileObject.metadata.push([key, metadataContents]);
    }
  }

  if (profile.getElementsByTagName('InputClaims').length > 0) {
    for (let inputClaim of Array.from(profile.getElementsByTagName('InputClaim'))) {
      let inputClaimId = getRequiredElementAttribute(inputClaim, 'ClaimTypeReferenceId', name, xmlTrace);

      let inputXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'InputClaim', inputClaimId);

      if (
        parsedXMLComponents.claims[inputClaimId] !== undefined ||
        checkInheritedForClaims(inheritedPolicies, inputClaimId)
      ) {
        let profileInputClaim = constuctInputClaim(inputClaim, name, inputXMLTrace);

        profileObject.inputClaims.push(profileInputClaim);
      } else {
        let cRefId = resolveClaimAgainstClaimsSchema(inputClaimId, parsedXMLComponents, inheritedPolicies);
        if (cRefId === undefined) {
          throw new ErrorXMLParsing(
            `Policy ${name}: InputClaim ${inputClaimId} in technical profile ${profileObject.id} does not map to an existing Claim`,
            inputXMLTrace,
          );
        }

        // Make all names uniform
        let profileInputClaim = constuctInputClaim(inputClaim, name, inputXMLTrace, cRefId);

        profileObject.inputClaims.push(profileInputClaim);
      }
    }
  }

  if (profile.getElementsByTagName('InputClaimsTransformations').length > 0) {
    for (let inputTransformation of Array.from(profile.getElementsByTagName('InputClaimsTransformation'))) {
      let transformationReferenceId = getRequiredElementAttribute(inputTransformation, 'ReferenceId', name, xmlTrace);

      let inputTransformationXMLTrace = ErrorXMLParsing.nextXMLTrace(
        xmlTrace,
        'InputClaimsTransformation',
        transformationReferenceId,
      );

      if (
        parsedXMLComponents.claimTransformations[transformationReferenceId] !== undefined ||
        checkInheritedForClaimsTransformation(inheritedPolicies, transformationReferenceId)
      ) {
        profileObject.inputClaimsTransformations.push(transformationReferenceId);
      } else {
        let cRefId = resolveClaimTransformationsAgainstClaimsTransformationSchema(
          transformationReferenceId,
          parsedXMLComponents,
          inheritedPolicies,
        );
        if (cRefId === undefined) {
          throw new ErrorXMLParsing(
            `Policy ${name}: InputClaimsTransformation ${transformationReferenceId} in technical profile ${profileObject.id} does not map to an existing ClaimTransformation`,
            inputTransformationXMLTrace,
          );
        }
      }
    }
  }

  if (profile.getElementsByTagName('OutputClaims').length > 0) {
    for (let outputClaim of Array.from(profile.getElementsByTagName('OutputClaim'))) {
      let outputClaimId = getRequiredElementAttribute(outputClaim, 'ClaimTypeReferenceId', name, xmlTrace);

      let outputXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'OutputClaim', outputClaimId);

      if (
        parsedXMLComponents.claims[outputClaimId] !== undefined ||
        checkInheritedForClaims(inheritedPolicies, outputClaimId)
      ) {
        let profileOutputClaim = constuctOutputClaim(outputClaim, name, outputXMLTrace);

        profileObject.outputClaims.push(profileOutputClaim);
      } else {
        let cRefId = resolveClaimAgainstClaimsSchema(outputClaimId, parsedXMLComponents, inheritedPolicies);
        if (cRefId === undefined) {
          throw new ErrorXMLParsing(
            `Policy ${name}: OutputClaim ${outputClaimId} in technical profile ${profileObject.id} does not map to an existing Claim`,
            outputXMLTrace,
          );
        }

        // Make all names uniform
        let profileOutputClaim = constuctOutputClaim(outputClaim, name, outputXMLTrace, cRefId);

        profileObject.outputClaims.push(profileOutputClaim);
      }
    }
  }

  if (profile.getElementsByTagName('OutputClaimsTransformations').length > 0) {
    for (let outputTransformation of Array.from(profile.getElementsByTagName('OutputClaimsTransformation'))) {
      let transformationReferenceId = getRequiredElementAttribute(outputTransformation, 'ReferenceId', name, xmlTrace);

      let outputTransformationXMLTrace = ErrorXMLParsing.nextXMLTrace(
        xmlTrace,
        'OutputClaimsTransformation',
        transformationReferenceId,
      );

      if (
        parsedXMLComponents.claimTransformations[transformationReferenceId] !== undefined ||
        checkInheritedForClaimsTransformation(inheritedPolicies, transformationReferenceId)
      ) {
        profileObject.outputClaimsTransformations.push(transformationReferenceId);
      } else {
        let cRefId = resolveClaimTransformationsAgainstClaimsTransformationSchema(
          transformationReferenceId,
          parsedXMLComponents,
          inheritedPolicies,
        );
        if (cRefId === undefined) {
          throw new ErrorXMLParsing(
            `Policy ${name}: OutputClaimsTransformation ${transformationReferenceId} in technical profile ${profileObject.id} does not map to an existing ClaimTransformation`,
            outputTransformationXMLTrace,
          );
        }
      }
    }
  }
};

const getTechnicalProfileDefinitionsFromPolicy = (
  xml: Readonly<Document>,
  parsedXMLComponents: Readonly<PolicyComponents>,
  name: string,
  inheritedPolicies: Readonly<Record<string, PolicyComponents>>,
): {
  selfAsserted: Record<string, SelfAssertedProfile>;
  other: Record<string, NonInteractiveTechnicalProfile>;
  claimsProviders: Array<ClaimProvider>;
} => {
  let tempSelfAssertedProfile: Record<string, SelfAssertedProfile> = {};
  let tempOtherProfiles: Record<string, NonInteractiveTechnicalProfile> = {};
  let claimProviders: Array<ClaimProvider> = [];
  const claimProviderElements = Array.from(xml.getElementsByTagName('ClaimsProvider'));
  let xmlTrace = `${name}: ClaimsProviders`;

  for (let claimProvider of claimProviderElements) {
    let newClaimProvider = init_ClaimProvider();
    const claimProviderDisplayName = getTextContentOfChild(
      claimProvider.getElementsByTagName('DisplayName')[0],
      name,
      xmlTrace,
    );

    newClaimProvider.displayName = claimProviderDisplayName;

    let claimProviderXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'ClaimsProvider', claimProviderDisplayName);

    let claimProviderDomain = getTextContentOfChild(
      claimProvider.getElementsByTagName('Domain')[0],
      name,
      claimProviderXMLTrace,
    );

    newClaimProvider.domain = claimProviderDomain;

    claimProviders.push(newClaimProvider);
    // Check for overloaded technical profiles (so we can have more distinct and useful profile names)
    let overloadedTechnicalProfiles: Array<Element> = [];
    // Find all self asserted technical profiles
    let selfAssertedPages: Array<Element> = [];
    let otherProfiles: Array<Element> = [];

    // Not sure this is catching all possible overloaded technical profiles
    for (let elem of Array.from(claimProvider.querySelectorAll('TechnicalProfiles>TechnicalProfile'))) {
      // Right now overlaoded profiles means that the profile includes another Technical Profile and has a Metadata Item with the key ContentDefinitionReferenceId
      if (
        elem.getElementsByTagName('IncludeTechnicalProfile').length > 0 &&
        Array.from(elem.getElementsByTagName('Item')).filter(
          (elemItem) => elemItem.getAttribute('Key') === 'ContentDefinitionReferenceId',
        ).length > 0
      )
        overloadedTechnicalProfiles.push(elem);
      else if (
        elem.getElementsByTagName('Protocol').length > 0 &&
        elem.getElementsByTagName('Protocol')[0].getAttribute('Handler')?.includes('.SelfAssertedAttributeProvider')
      )
        selfAssertedPages.push(elem);
      else otherProfiles.push(elem);
    }

    for (let profile of otherProfiles) {
      const profileName = getRequiredElementAttribute(profile, 'Id', name, claimProviderXMLTrace);

      let tpXMLTrace = ErrorXMLParsing.nextXMLTrace(
        claimProviderXMLTrace,
        'TechnicalProfiles>TechnicalProfile',
        profileName,
      );

      tempOtherProfiles[profileName] = init_NonInteractiveTechnicalProfile();
      tempOtherProfiles[profileName].id = profileName;
      tempOtherProfiles[profileName].claimProvider = claimProviderDisplayName;

      mapCommonTPTags(
        profile,
        tempOtherProfiles[profileName],
        parsedXMLComponents,
        tpXMLTrace,
        name,
        inheritedPolicies,
      );

      if (profile.getElementsByTagName('PersistedClaims').length > 0) {
        for (let persistedClaim of Array.from(profile.getElementsByTagName('PersistedClaim'))) {
          let persistedClaimId = getRequiredElementAttribute(persistedClaim, 'ClaimTypeReferenceId', name, tpXMLTrace);
          let persistedXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'PersistedClaim', persistedClaimId);

          if (
            parsedXMLComponents.claims[persistedClaimId] !== undefined ||
            checkInheritedForClaims(inheritedPolicies, persistedClaimId)
          ) {
            let profilePersistedClaim: PersistedClaim = {
              claimTypeReferenceId: persistedClaimId,
              partnerClaimType: getPossibleElementAttribute(persistedClaim, 'PartnerClaimType'),
              defaultValue: getPossibleElementAttribute(persistedClaim, 'DefaultValue'),
            };
            tempOtherProfiles[profileName].persistedClaims.push(profilePersistedClaim);
          } else {
            let cRefId = resolveClaimAgainstClaimsSchema(persistedClaimId, parsedXMLComponents, inheritedPolicies);
            if (cRefId === undefined) {
              throw new ErrorXMLParsing(
                `Polciy ${name}: PersistedClaim ${persistedClaimId} in technical profile ${tempOtherProfiles[profileName].id} does not map to an existing Claim`,
                persistedXMLTrace,
              );
            }
            // Make all names uniform
            persistedClaim.setAttribute('ClaimTypeReferenceId', cRefId);
            let profilePersistedClaim: PersistedClaim = {
              claimTypeReferenceId: persistedClaimId,
              partnerClaimType: getPossibleElementAttribute(persistedClaim, 'PartnerClaimType'),
              defaultValue: getPossibleElementAttribute(persistedClaim, 'DefaultValue'),
            };

            tempOtherProfiles[profileName].persistedClaims.push(profilePersistedClaim);
          }
        }
      }
    }

    // Create an entry for each self asserted technical profile with the defined claims
    for (let profile of selfAssertedPages) {
      const profileName = getRequiredElementAttribute(profile, 'Id', name, claimProviderXMLTrace);

      let tpXMLTrace = ErrorXMLParsing.nextXMLTrace(
        claimProviderXMLTrace,
        'TechnicalProfiles>TechnicalProfile',
        profileName,
      );

      tempSelfAssertedProfile[profileName] = init_SelfAssertedProfile();
      tempSelfAssertedProfile[profileName].id = profileName;
      tempSelfAssertedProfile[profileName].claimProvider = claimProviderDisplayName;

      mapCommonTPTags(
        profile,
        tempSelfAssertedProfile[profileName],
        parsedXMLComponents,
        tpXMLTrace,
        name,
        inheritedPolicies,
      );

      if (profile.getElementsByTagName('DisplayClaims').length > 0) {
        for (let displayClaim of Array.from(profile.getElementsByTagName('DisplayClaim'))) {
          let profileDisplayClaim: DisplayClaim = init_DisplayClaim();

          let displayControlReferenceId = getPossibleElementAttribute(displayClaim, 'DisplayControlReferenceId');
          let claimTypeReferenceId = getPossibleElementAttribute(displayClaim, 'ClaimTypeReferenceId');

          if (displayControlReferenceId === '' && claimTypeReferenceId === '') {
            throw new ErrorXMLParsing(
              `Policy ${name}: Display claim in technical profile ${profileName} is missing DisplayControlReferenceId or ClaimTypeReferenceId: ${serializerForErrors.serializeToString(
                displayClaim,
              )}`,
              tpXMLTrace,
            );
          }

          profileDisplayClaim.displayControlReferenceId = displayControlReferenceId;
          profileDisplayClaim.claimTypeReferenceId = claimTypeReferenceId;
          profileDisplayClaim.required = getPossibleElementAttribute(displayClaim, 'Required') === 'true';

          tempSelfAssertedProfile[profileName].displayClaims.push(profileDisplayClaim);
        }
      }

      if (profile.getElementsByTagName('ValidationTechnicalProfiles').length > 0) {
        let validationTPs = getValidationTPOfPolicy(profile, name, tpXMLTrace);

        tempSelfAssertedProfile[profileName].validationTechnicalProfiles = validationTPs;
      }
    } // end of Create an entry for each self asserted technical profile

    // This may be incomplete
    for (let overloadedProfile of overloadedTechnicalProfiles) {
      if (overloadedProfile.getElementsByTagName('IncludeTechnicalProfile').length > 0) {
        let overloadedProfileBase = getRequiredElementAttribute(
          overloadedProfile.getElementsByTagName('IncludeTechnicalProfile')[0],
          'ReferenceId',
          name,
          claimProviderXMLTrace,
        );

        if (tempSelfAssertedProfile[overloadedProfileBase] !== undefined) {
          let overloadedProfileName = getRequiredElementAttribute(overloadedProfile, 'Id', name, claimProviderXMLTrace);

          tempSelfAssertedProfile[overloadedProfileName] = { ...tempSelfAssertedProfile[overloadedProfileBase] };
        }
      }
    } // End of get overloaded technical profiles}
  }

  return { selfAsserted: tempSelfAssertedProfile, other: tempOtherProfiles, claimsProviders: claimProviders };
}; // End of getTechnicalProfileDefinitionsFromPolicy

const getValidationTPOfPolicy = (
  selfAssertedTP: Readonly<Element>,
  name: string,
  xmlTrace: string,
): Array<ValidationTP> => {
  let validationTPs: Array<ValidationTP> = [];
  if (selfAssertedTP.getElementsByTagName('ValidationTechnicalProfiles').length > 0) {
    for (let validationTechnicalProfile of Array.from(
      selfAssertedTP.getElementsByTagName('ValidationTechnicalProfile'),
    )) {
      let newValidationTP = init_ValidationTP();
      let validationName = getRequiredElementAttribute(validationTechnicalProfile, 'ReferenceId', name, xmlTrace);

      let validationTPXMLTrace = ErrorXMLParsing.nextXMLTrace(
        xmlTrace,
        'ValidationTechnicalProfiles>ValidationTechnicalProfile',
        validationName,
      );

      newValidationTP.id = validationName;
      newValidationTP.continueOnError =
        getPossibleElementAttribute(validationTechnicalProfile, 'ContinueOnError') === 'true';
      // Continue on success is weird, but essentially the default is true. So if the attribute is not defined we want ti to be true
      newValidationTP.continueOnSuccess =
        getPossibleElementAttribute(validationTechnicalProfile, 'ContinueOnSuccess') !== 'false';
      if (validationTechnicalProfile.getElementsByTagName('Precondition').length > 0) {
        newValidationTP.preconditions = getPreconditions(
          validationTechnicalProfile,
          PreconditionAction.SKIPVALIDATIONTECHINCALPROFILE,
          name,
          validationTPXMLTrace,
        );
      }

      validationTPs.push(newValidationTP);
    }
  }

  return validationTPs;
};

const getUserJourneysFromPolicy = (xml: Readonly<Document>, name: string): Array<UserJourney> => {
  // Construct user journeys
  let userJourneysXML = Array.from(xml.getElementsByTagName('UserJourney'));
  let xmlTrace = 'UserJourneys';

  let tempUserJourneys: Array<UserJourney> = [];

  // <UserJourney Id=''>
  for (let journey of userJourneysXML) {
    let newJourney = init_UserJourney();
    let journeyId = getRequiredElementAttribute(journey, 'Id', name, xmlTrace);

    let userJourneyXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'UserJourney', journeyId);

    newJourney.id = journeyId;

    newJourney.defaultCpimIssuerTechnicalProfileReferenceId = getPossibleElementAttribute(
      journey,
      'DefaultCpimIssuerTechnicalProfileReferenceId',
    );

    if (journey.getElementsByTagName('AuthorizationTechnicalProfile').length > 0) {
      newJourney.authorizationTP = getPossibleElementAttribute(journey, 'ReferenceId');
    }

    // <OrchestrationStep Order="" Type="ClaimsExchange">
    for (let orchestrationStep of Array.from(journey.getElementsByTagName('OrchestrationStep'))) {
      let newOrchestrationStep = getOrchestrationStep(orchestrationStep, name, userJourneyXMLTrace);

      newJourney.orchestrationSteps.push(newOrchestrationStep);
    }

    tempUserJourneys.push(newJourney);
  } // end user journey creation

  return tempUserJourneys;
};

const getSubJourneysFromPolicy = (xml: Readonly<Document>, name: string): Array<SubJourney> => {
  let tempSubJourneys: Array<SubJourney> = [];
  let xmlTrace = 'SubJourneys';

  // <SubJourney Id="" Type="Call">
  for (let subJourney of Array.from(xml.getElementsByTagName('SubJourney'))) {
    let newSubJourney = init_SubJourney();

    let subJourneyId = getRequiredElementAttribute(subJourney, 'Id', name, xmlTrace);
    let subJourneyXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'SubJourney', subJourneyId);

    let subJourneyType = getRequiredElementAttribute(subJourney, 'Type', name, subJourneyXMLTrace);

    newSubJourney.type = subJourneyType;
    newSubJourney.id = subJourneyId;

    // <OrchestrationStep Order="" Type="ClaimsExchange">
    for (let orchestrationStep of Array.from(subJourney.getElementsByTagName('OrchestrationStep'))) {
      let newOrchestrationStep = getOrchestrationStep(orchestrationStep, name, subJourneyXMLTrace);
      newSubJourney.orchestrationSteps.push(newOrchestrationStep);
    }

    tempSubJourneys.push(newSubJourney);
  }

  return tempSubJourneys;
}; // end of getSubJourneysFromPolicy

const getPreconditions = (
  element: Element,
  preconditionAction: PreconditionAction,
  name: string,
  xmlTrace: string,
): Array<Precondition> => {
  let preconditionChildren = element.getElementsByTagName('Precondition');
  let preconditions: Array<Precondition> = [];

  for (let pc of Array.from(preconditionChildren)) {
    let newPrecondition =
      preconditionAction === PreconditionAction.SKIPORCHESTRATIONSTEP
        ? init_PreconditionOrchestration()
        : init_PreconditionTP();
    let preconditionType = stringToPreconditionType(getPossibleElementAttribute(pc, 'Type'), xmlTrace);
    newPrecondition.executeActionsIf = getPossibleElementAttribute(pc, 'ExecuteActionsIf') === 'true';
    for (let elem of Array.from(pc.getElementsByTagName('Value'))) {
      let val = getTextContentOfChild(elem, name, xmlTrace);
      newPrecondition.values.push(val);
    }

    newPrecondition.type = preconditionType;
    preconditions.push(newPrecondition);
  }

  return preconditions;
}; // end of getPreconditions

const getOrchestrationStep = (element: Element, name: string, xmlTrace: string): OrchestrationStep => {
  let newOrchestrationStep = init_OrchestrationStep();
  let order = element.getAttribute('Order');

  if (order === null) {
    throw new ErrorXMLParsing(`Policy ${name}: Invalid order on orchestration step`, xmlTrace);
  }

  newOrchestrationStep.order = parseInt(order);

  let orechstrationStepXMLTrace = ErrorXMLParsing.nextXMLTrace(xmlTrace, 'OrchestrationSteps>OrchestrationStep', order);

  newOrchestrationStep.type = getPossibleElementAttribute(element, 'Type');
  newOrchestrationStep.contentDefinitonReferenceId = getPossibleElementAttribute(
    element,
    'ContentDefinitionReferenceId',
  );
  newOrchestrationStep.cpimIssuerTechnicalProfileReferenceId = getPossibleElementAttribute(
    element,
    'CpimIssuerTechnicalProfileReferenceId',
  );
  let orchestrationPreconditions = getPreconditions(
    element,
    PreconditionAction.SKIPORCHESTRATIONSTEP,
    name,
    orechstrationStepXMLTrace,
  );

  newOrchestrationStep.preconditions = orchestrationPreconditions;
  if (newOrchestrationStep.type !== 'InvokeSubJourney') {
    // <ClaimsExchange Id="" TechnicalProfileReferenceId="" />
    for (let claimsExchange of Array.from(element.getElementsByTagName('ClaimsExchange'))) {
      let newClaimsExchange = init_ClaimsExhange();
      let claimsExchangeTPReference = getRequiredElementAttribute(
        claimsExchange,
        'TechnicalProfileReferenceId',
        name,
        orechstrationStepXMLTrace,
      );

      newClaimsExchange.technicalProfilerefrenceId = claimsExchangeTPReference;
      newClaimsExchange.id = getPossibleElementAttribute(claimsExchange, 'Id');
      newOrchestrationStep.claimsExchanges.push(newClaimsExchange);
    }

    if (element.getElementsByTagName('ClaimsProviderSelections').length > 0) {
      let claimsProviderSelections = element.getElementsByTagName('ClaimsProviderSelections')[0];

      newOrchestrationStep.claimsProviderSelections.displayOptions = getPossibleElementAttribute(
        claimsProviderSelections,
        'DisplayOption',
      );
      for (let provider of Array.from(element.getElementsByTagName('ClaimsProviderSelection'))) {
        let newProviderSelection = init_ProviderSelect();
        newProviderSelection.targetClaimExchangeId =
          getPossibleElementAttribute(provider, 'TargetClaimsExchangeId') ?? '';
        newProviderSelection.validationClaimsExchange =
          getPossibleElementAttribute(provider, 'ValidationClaimsExchangeId') ?? '';
        newOrchestrationStep.claimsProviderSelections.providers.push(newProviderSelection);
      }
    }
  }
  // Check subjourney contents
  else if (element.getElementsByTagName('Candidate').length > 0) {
    newOrchestrationStep.subJourneyToInvoke = getPossibleElementAttribute(
      element.getElementsByTagName('Candidate')[0],
      'SubJourneyReferenceId',
    );
  }

  return newOrchestrationStep;
}; // end of getOrchestrationStep
